import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
    addEdge,
    type Connection,
    Controls,
    type Edge,
    Handle,
    type Node,
    type NodeChange,
    Position,
    useEdgesState,
    useNodesState
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { components } from '../../../types/api-generated';
import { parseSVGPorts, type SVGUnitConfig } from './svgPortParser';
import { UNIT_SVG_MAP, type UnitType } from './unitOperationsConfig';

// Simple global position cache - stores node positions by node name
const nodePositionCache = new Map<string, { x: number; y: number }>();

// Unit operations hook defined locally
interface UnitOperationConfig extends SVGUnitConfig {
  svgContent: string;
  color: string;
}

const svgCache = new Map<string, UnitOperationConfig>();

function useUnitOperations() {
  const loadUnitConfig = useCallback(async (unitType: UnitType): Promise<UnitOperationConfig> => {
    const svgPath = UNIT_SVG_MAP[unitType];
    
    // Check cache first
    if (svgCache.has(svgPath)) {
      return svgCache.get(svgPath)!;
    }

    try {
      const response = await fetch(svgPath);
      if (!response.ok) {
        throw new Error(`Failed to load SVG: ${svgPath} (Status: ${response.status})`);
      }
      
      const svgContent = await response.text();
      const config = parseSVGPorts(svgContent);
        const unitConfig: UnitOperationConfig = {
        ...config,
        svgContent,
        color: "black"
      };
      
      // Cache the result
      svgCache.set(svgPath, unitConfig);
      
      return unitConfig;
    } catch (err) {      // Cache the error to prevent repeated attempts
      const errorConfig: UnitOperationConfig = {
        width: 60, 
        height: 60,
        viewBox: "0 0 60 60",
        svgContent: `<svg width="60" height="60" viewBox="0 0 60 60"><rect x="5" y="5" width="50" height="50" fill="#fee2e2" stroke="#dc2626" stroke-width="2"/><text x="30" y="30" text-anchor="middle" font-size="8" fill="#dc2626" font-weight="bold">ERROR</text><text x="30" y="40" text-anchor="middle" font-size="6" fill="#dc2626">${unitType}</text></svg>`,
        ports: [
          { id: "inlet-1", type: "input", x: 0, y: 30 },
          { id: "outlet-1", type: "output", x: 60, y: 30 }
        ],
        color: "#dc2626"
      };
      svgCache.set(svgPath, errorConfig);
      throw new Error(`Error loading unit operation ${unitType}: ${err}`);
    }
  }, []);
  
  return { loadUnitConfig };
}

// Use backend-generated types
type FlowsheetData = components["schemas"]["AgentFlowsheetOutput"];
type StreamType = "Mass" | "Energy" | "Signal"; // From backend StreamType enum

interface FlowsheetProps {
  flowsheetData: FlowsheetData;
}

// Modular CustomNode component that loads SVGs dynamically
const CustomNode = ({ data }: { data: any }) => {
  const { loadUnitConfig } = useUnitOperations();
  const [unitConfig, setUnitConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Don't retry if we already have an error
    if (hasError) return;

    const loadConfig = async () => {
      try {
        setLoading(true);
        const config = await loadUnitConfig(data.unit_type);
        setUnitConfig(config);
        setHasError(false);
      } catch (error) {
        console.error('Failed to load unit config:', error);
        setHasError(true);
        // Fallback to inline SVG if loading fails
        setUnitConfig(getFallbackConfig(data.unit_type));
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [data.unit_type]); // Removed loadUnitConfig dependency to prevent infinite loops  // Fallback configuration for when SVG loading fails
  const getFallbackConfig = (unitType: UnitType): UnitOperationConfig => {
    return {
      width: 60, 
      height: 60,
      viewBox: "0 0 60 60",
      svgContent: `<svg width="60" height="60" viewBox="0 0 60 60"><rect x="5" y="5" width="50" height="50" fill="none" stroke="black" stroke-width="2"/><text x="30" y="35" text-anchor="middle" font-size="8" fill="black" font-weight="bold">${unitType.toUpperCase()}</text></svg>`,
      ports: [
        { id: "inlet-1", type: "input", x: 0, y: 30 },
        { id: "outlet-1", type: "output", x: 60, y: 30 }
      ],
      color: "black"
    };
  };
  if (loading || !unitConfig) {
    return (
      <div className="flex items-center justify-center w-16 h-16 bg-gray-200 rounded">
        <div className="text-xs">Loading...</div>
      </div>
    );
  }

  // Show retry option for error states
  if (hasError && unitConfig?.color === "#dc2626") {
    return (
      <div className="relative">
        <div 
          className="relative"
          style={{ 
            width: unitConfig.width + 'px',
            height: unitConfig.height + 'px'
          }}
        >
          <div 
            dangerouslySetInnerHTML={{ 
              __html: unitConfig.svgContent
            }}
            style={{ width: '100%', height: '100%' }}
          />
          
          {/* Retry button */}
          <button
            onClick={() => {
              setHasError(false);
              setLoading(true);
            }}
            className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded border"
            title="Retry loading SVG"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const inputPorts = unitConfig.ports.filter((port: any) => port.type === 'input');
  const outputPorts = unitConfig.ports.filter((port: any) => port.type === 'output');

  return (
    <div 
      className="relative"
      style={{ 
        width: unitConfig.width + 'px',
        height: unitConfig.height + 'px'
      }}
    >
      {/* Input handles positioned based on SVG ports */}
      {inputPorts.map((port: any, index: number) => (
        <Handle
          key={`input-${index}`}
          type="target"
          position={Position.Left}
          id={port.id}
          style={{ 
            left: `${(port.x / unitConfig.width) * 100}%`,
            top: `${(port.y / unitConfig.height) * 100}%`,
            background: '#333',
            border: '1px solid white',
            width: '8px',
            height: '8px',
            transform: 'translate(-50%, -50%)'
          }}
        />
      ))}      {/* SVG content - no color replacement needed */}
      <div 
        dangerouslySetInnerHTML={{ 
          __html: unitConfig.svgContent
        }}
        style={{ width: '100%', height: '100%' }}
      />      {/* Node label and variables */}
      <div 
        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full"
        style={{ 
          fontSize: '10px', 
          color: '#333',
          textAlign: 'center',
          marginTop: '4px',
          minWidth: '120px',
          padding: '6px 8px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Unit operation name */}
        <div 
          style={{ 
            fontWeight: 'bold',
            marginBottom: '2px'
          }}
        >
          {data.name}
        </div>
        
        {/* Variables in individual boxes */}
        {data.variables && data.variables.length > 0 && (
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '2px', 
            justifyContent: 'flex-start',
            fontSize: '8px'
          }}>
            {data.variables.map((variable: string, index: number) => (
              <div 
                key={index} 
                style={{ 
                  padding: '2px 4px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '7px',
                  whiteSpace: 'nowrap'
                }}
              >
                {variable}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Output handles positioned based on SVG ports */}
      {outputPorts.map((port: any, index: number) => (
        <Handle
          key={`output-${index}`}
          type="source"
          position={Position.Right}
          id={port.id}
          style={{ 
            left: `${(port.x / unitConfig.width) * 100}%`,
            top: `${(port.y / unitConfig.height) * 100}%`,
            background: '#333',
            border: '1px solid white',
            width: '8px',
            height: '8px',
            transform: 'translate(-50%, -50%)'
          }}
        />
      ))}
    </div>
  );
};

// Custom edge component that displays variables as bullet points
const CustomEdge = ({ id, sourceX, sourceY, targetX, targetY, data }: any) => {
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;
  
  const edgePath = `M${sourceX},${sourceY} L${midX},${sourceY} L${midX},${targetY} L${targetX},${targetY}`;

  // Calculate midpoint for label placement

  
  const edgeStyle = getEdgeStyle(data?.stream_type || 'Mass');
  
  return (
    <>
      <path
        id={id}
        style={edgeStyle}
        className="react-flow__edge-path"
        d={edgePath}
        fill="none"
      />
        {/* Edge label with variables */}
      {(data?.edge_name || (data?.variables && data.variables.length > 0)) && (
        <foreignObject
          x={midX - 60}
          y={midY - 20}
          width="120"
          height="40"
          style={{ overflow: 'visible' }}        ><div
            style={{
              fontSize: '10px',
              textAlign: 'center',
              minWidth: 'max-content',
              padding: '6px 8px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Edge name */}
            {data?.edge_name && (
              <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                {data.edge_name}
              </div>
            )}
              {/* Variables in individual boxes */}
            {data?.variables && data.variables.length > 0 && (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '2px', 
                justifyContent: 'flex-start',
                fontSize: '8px'
              }}>
                {data.variables.map((variable: string, index: number) => (
                  <div 
                    key={index} 
                    style={{ 
                      padding: '2px 4px',
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '7px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {variable}
                  </div>
                ))}
              </div>
            )}
          </div>
        </foreignObject>
      )}
    </>
  );
};

// Custom edge component for different stream types
const getEdgeStyle = (streamType: StreamType) => {
  const styleMap: Record<StreamType, any> = {
    "Mass": { stroke: '#2563eb', strokeWidth: 3 },
    "Energy": { stroke: '#dc2626', strokeWidth: 2, strokeDasharray: '5,5' },
    "Signal": { stroke: '#16a34a', strokeWidth: 1, strokeDasharray: '2,2' }
  };
  return styleMap[streamType] || { stroke: '#6b7280', strokeWidth: 2 };
};

// Node types for React Flow
const nodeTypes = {
  custom: CustomNode,
};

// Edge types for React Flow
const edgeTypes = {
  custom: CustomEdge,
};

const Flowsheet: React.FC<FlowsheetProps> = ({ flowsheetData }) => {
  // Convert flowsheet data to React Flow format
  const { nodes: flowNodes, edges: flowEdges } = useMemo(() => {
    console.log('[Flowsheet] Received flowsheetData:', flowsheetData);
    
    if (!flowsheetData || !flowsheetData.nodes || !flowsheetData.edges) {
      console.log('[Flowsheet] Missing data - nodes or edges are undefined');      return { nodes: [], edges: [] };
    }
    
    // Node positioning: Use backend positions if provided, otherwise fall back to auto-grid layout
    // Users can still drag nodes to reposition them regardless of initial placement
    const gridCols = Math.ceil(Math.sqrt(flowsheetData.nodes.length));
    const nodeSpacing = 250; // Increased spacing to accommodate variables display
    
    const nodes: Node[] = flowsheetData.nodes.map((node, index) => {
      let position;
      
      // Priority: 1. Cached position, 2. Backend position, 3. Grid layout
      if (nodePositionCache.has(node.name)) {
        position = nodePositionCache.get(node.name)!;
        console.log(`[Flowsheet] Using cached position for ${node.name}:`, position);
      } else if (node.pos && node.pos.length === 2) {
        position = { x: node.pos[0], y: node.pos[1] };
        console.log(`[Flowsheet] Using backend position for ${node.name}:`, position);
      } else {
        // Fallback to grid layout
        const row = Math.floor(index / gridCols);
        const col = index % gridCols;
        position = { 
          x: col * nodeSpacing + 100, 
          y: row * nodeSpacing + 100 
        };
        console.log(`[Flowsheet] Using grid position for ${node.name}:`, position);
      }
      
      return {
        id: node.name,
        type: 'custom',
        position,
        data: {
          name: node.name,
          unit_type: node.unit_type,
          variables: node.variables
        },
      };
    });const edges: Edge[] = flowsheetData.edges.map((edge, index) => {
      // Determine which handles to connect based on unit types and edge index
      const sourceNode = flowsheetData.nodes.find(n => n.name === edge.source_name);
      const targetNode = flowsheetData.nodes.find(n => n.name === edge.target_name);
      
      // Default handle IDs
      let sourceHandle = "outlet-1";
      let targetHandle = "inlet-1";
      
      // For nodes with multiple outputs (like Separator), use different handles
      if (sourceNode?.unit_type === "Separator") {
        sourceHandle = index % 2 === 0 ? "outlet-1" : "outlet-2";
      }
      
      // For nodes with multiple inputs (like Reactor, Mixer), use different handles
      if (targetNode?.unit_type === "Reactor") {
        targetHandle = index % 2 === 0 ? "inlet-1" : "inlet-2";
      } //else if (targetNode?.unit_type === "Mixer") {
        //const inputIndex = index % 2;
        //targetHandle = inputIndex === 0 ? "inlet-1" : "inlet-2";
      //}
          return {
        id: `${edge.source_name}-${edge.target_name}-${index}`,
        source: edge.source_name,
        target: edge.target_name,
        sourceHandle: sourceHandle,
        targetHandle: targetHandle,
        type: 'custom',
        data: {
          edge_name: edge.edge_name,
          variables: edge.variables,
          stream_type: edge.stream_type
        },
        style: getEdgeStyle(edge.stream_type),
      };
    });

    console.log('[Flowsheet] Created nodes:', nodes);
    console.log('[Flowsheet] Created edges:', edges);
    console.log('[Flowsheet] Input edges data:', flowsheetData.edges);    return { nodes, edges };
  }, [flowsheetData]);
    const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Custom handler to save node positions to cache when they're moved
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    onNodesChange(changes);
    
    // Save positions to cache when nodes are moved
    changes.forEach(change => {
      if (change.type === 'position' && change.position && change.id) {
        nodePositionCache.set(change.id, change.position);
      }
    });
  }, [onNodesChange]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Update nodes and edges when flowsheetData changes
  React.useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  if (!flowsheetData || !flowsheetData.nodes || flowsheetData.nodes.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-4">📊</div>
          <div className="text-lg font-medium">No Flowsheet Data</div>
          <div className="text-sm">Upload a flowsheet or ask the AI to create one</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-800">Process Flowsheet</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 relative">
        <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }} // Add padding for better view when using backend positions
        attributionPosition="bottom-left"
        nodesDraggable={true} // Explicitly enable node dragging (default is true)
      >
        <Controls />
      </ReactFlow>
      
      {/* Legend */}
      <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-lg border max-w-xs">
        <h3 className="font-bold text-sm mb-2">Stream Types</h3>
        <div className="space-y-1 text-xs">
          <div className="flex items-center">
            <div className="w-4 h-0.5 bg-blue-600 mr-2"></div>
            <span>Mass Flow</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-0.5 bg-red-600 border-dashed border-t mr-2"></div>
            <span>Energy Flow</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-0.5 bg-green-600 border-dotted border-t mr-2"></div>
            <span>Signal Flow</span>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Flowsheet;